import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearAuthData } from '../utils/authUtils';

// Custom baseQuery with error handling
const baseQueryWithAuth = fetchBaseQuery({
    baseUrl: "https://unidestinapi.ini.az/api/",
    prepareHeaders: (headers) => {
        const token = localStorage.getItem('token') || ""
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
    },
});

const baseQueryWithLogout = async (args, api, extraOptions) => {
    let result = await baseQueryWithAuth(args, api, extraOptions);
    
    // Handle 401 Unauthorized - token is invalid or expired
    if (result.error && result.error.status === 401) {
        // Clean up localStorage using utility function
        clearAuthData();
        
        // Reload the page to trigger authentication check
        window.location.reload();
    }
    
    return result;
};

export const api = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithLogout,
    tagTypes: ['country', 'city', 'university', 'program', 'studentRequestStatus', 'scholarship', 'consultant', 'role', 'permission', 'student', 'studentApplication', 'applicationSource', 'educationLevel', 'currency', 'statusChange', 'universityOffer', 'service', 'contract', 'contractStatus', 'paymentStatus', 'serviceType', 'cashTransaction', 'transactionType', 'transactionCategory', 'paymentMethod', 'task'],
    endpoints: (builder) => ({
        fetchLogin: builder.mutation({
            query: (obj) => ({
                url: `login`,
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify(obj)
            })
        }),
     
        getCities: builder.query({
            query: (filter) => ({
                url: `cities`,
                params: filter
            }),
            providesTags: ["city"]
        }),
        getCity: builder.query({
            query: (id) => ({
                url: `cities/${id}`
            })
        }),
        updateCity: builder.mutation({
            query: ({ id, obj }) => ({
                url: `cities/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ["city"]
        }),
        addCity: builder.mutation({
            query: (obj) => ({
                url: `cities`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ["city"]
        }),
        deleteCity: builder.mutation({
            query: (id) => ({
                url: `cities/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["city"]
        }),
        getCountries: builder.query({
            query: (params) => ({
                url: `countries`,
                params
            }),
            providesTags: ['country']
        }),
        getCountry: builder.query({
            query: (id) => ({
                url: `countries/${id}`
            })
        }),
        getActiveCountries: builder.query({
            query: (params) => ({
                url: `countries-active`,
                params
            }),
            providesTags: ['country']
        }),
        updateCountry: builder.mutation({
            query: ({ id, obj }) => ({
                url: `countries/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['country']
        }),
        addCountry: builder.mutation({
            query: (obj) => ({
                url: `countries`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['country']
        }),
        deleteCountry: builder.mutation({
            query: (id) => ({
                url: `countries/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["country"]
        }),
        getUniversities: builder.query({
            query: (params) => ({
                url: `universities`,
                params
            }),
            providesTags: ['university']
        }),
        getActiveUniversities: builder.query({
            query: (params) => ({
                url: `universities-active`,
                params
            }),
            providesTags: ['university']
        }),
        getUniversity: builder.query({
            query: (id) => ({
                url: `universities/${id}`
            })
        }),
        updateUniversity: builder.mutation({
            query: ({ id, obj }) => ({
                url: `universities/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['university']
        }),
        addUniversity: builder.mutation({
            query: (obj) => ({
                url: `universities`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['university']
        }),
        deleteUniversity: builder.mutation({
            query: (id) => ({
                url: `universities/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["university"]
        }),

        // University Programs endpoints
        getPrograms: builder.query({
            query: (params) => ({
                url: `university-programs`,
                params
            }),
            providesTags: ['program']
        }),
        getActivePrograms: builder.query({
            query: (params) => ({
                url: `university-programs-active`,
                params
            }),
            providesTags: ['program']
        }),
        getProgram: builder.query({
            query: (id) => ({
                url: `university-programs/${id}`
            })
        }),
        updateProgram: builder.mutation({
            query: ({ id, obj }) => ({
                url: `university-programs/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['program']
        }),
        addProgram: builder.mutation({
            query: (obj) => ({
                url: `university-programs`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['program']
        }),
        deleteProgram: builder.mutation({
            query: (id) => ({
                url: `university-programs/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["program"]
        }),

        // Student Request Statuses endpoints
        getStudentRequestStatuses: builder.query({
            query: (params) => ({
                url: `student-request-statuses`,
                params
            }),
            providesTags: ['studentRequestStatus']
        }),
        getStudentRequestStatus: builder.query({
            query: (id) => ({
                url: `student-request-statuses/${id}`
            })
        }),
        addStudentRequestStatus: builder.mutation({
            query: (obj) => ({
                url: `student-request-statuses`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['studentRequestStatus']
        }),
        updateStudentRequestStatus: builder.mutation({
            query: ({ id, obj }) => ({
                url: `student-request-statuses/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['studentRequestStatus']
        }),
        deleteStudentRequestStatus: builder.mutation({
            query: (id) => ({
                url: `student-request-statuses/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["studentRequestStatus"]
        }),

        // Scholarships endpoints
        getScholarships: builder.query({
            query: (params) => ({
                url: `scholarships`,
                params
            }),
            providesTags: ['scholarship']
        }),
        getActiveScholarships: builder.query({
            query: (params) => ({
                url: `scholarships-active`,
                params
            }),
            providesTags: ['scholarship']
        }),
        getScholarship: builder.query({
            query: (id) => ({
                url: `scholarships/${id}`
            })
        }),
        updateScholarship: builder.mutation({
            query: ({ id, obj }) => ({
                url: `scholarships/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['scholarship']
        }),
        addScholarship: builder.mutation({
            query: (obj) => ({
                url: `scholarships`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['scholarship']
        }),
        deleteScholarship: builder.mutation({
            query: (id) => ({
                url: `scholarships/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["scholarship"]
        }),

        // Users endpoints
        getUsers: builder.query({
            query: (params) => ({
                url: `users`,
                params
            }),
            providesTags: ['user']
        }),
        getActiveUsers: builder.query({
            query: (params) => ({
                url: `users-active`,
                params
            }),
            providesTags: ['user']
        }),
        getUser: builder.query({
            query: (id) => ({
                url: `users/${id}`
            })
        }),
        updateUser: builder.mutation({
            query: ({ id, obj }) => ({
                url: `users/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['user']
        }),
        addUser: builder.mutation({
            query: (obj) => ({
                url: `users`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['user']
        }),
        deleteUser: builder.mutation({
            query: (id) => ({
                url: `users/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["user"]
        }),

        // Roles endpoints
        getRoles: builder.query({
            query: (params) => ({
                url: `roles`,
                params
            }),
            providesTags: ['role']
        }),
        getRole: builder.query({
            query: (id) => ({
                url: `roles/${id}`
            })
        }),
        addRole: builder.mutation({
            query: (obj) => ({
                url: `roles`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['role']
        }),
        updateRole: builder.mutation({
            query: ({ id, obj }) => ({
                url: `roles/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['role']
        }),
        deleteRole: builder.mutation({
            query: (id) => ({
                url: `roles/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["role"]
        }),

        // Permissions endpoints
        getPermissions: builder.query({
            query: (params) => ({
                url: `permissions`,
                params
            }),
            providesTags: ['permission']
        }),
        getPermission: builder.query({
            query: (id) => ({
                url: `permissions/${id}`
            })
        }),
        getGroupedPermissions: builder.query({
            query: () => ({
                url: `permissions/grouped`
            }),
            providesTags: ['permission']
        }),
        getPermissionModules: builder.query({
            query: () => ({
                url: `permissions/modules`
            }),
            providesTags: ['permission']
        }),
        addPermission: builder.mutation({
            query: (obj) => ({
                url: `permissions`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['permission']
        }),
        updatePermission: builder.mutation({
            query: ({ id, obj }) => ({
                url: `permissions/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['permission']
        }),
        deletePermission: builder.mutation({
            query: (id) => ({
                url: `permissions/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["permission"]
        }),

        // Consultants endpoints
        getConsultants: builder.query({
            query: (params) => ({
                url: `consultants`,
                params
            }),
            providesTags: ['consultant']
        }),
        getConsultant: builder.query({
            query: (id) => ({
                url: `consultants/${id}`
            })
        }),
        addConsultant: builder.mutation({
            query: (obj) => ({
                url: `consultants`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['consultant']
        }),
        updateConsultant: builder.mutation({
            query: ({ id, obj }) => ({
                url: `consultants/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['consultant']
        }),
        deleteConsultant: builder.mutation({
            query: (id) => ({
                url: `consultants/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["consultant"]
        }),

        // Get users with is_consultant = 1 for consultant admin selection
        getConsultantAdmins: builder.query({
            query: () => ({
                url: `users-consultants`,
            }),
            providesTags: ['user']
        }),

        // Students endpoints
        getStudents: builder.query({
            query: (params) => ({
                url: `students`,
                params
            }),
            providesTags: ['student']
        }),
        getActiveStudents: builder.query({
            query: (params) => ({
                url: `students-active`,
                params
            }),
            providesTags: ['student']
        }),
        getStudent: builder.query({
            query: (id) => ({
                url: `students/${id}`
            })
        }),
        addStudent: builder.mutation({
            query: (obj) => ({
                url: `students`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['student']
        }),
        updateStudent: builder.mutation({
            query: ({ id, obj }) => ({
                url: `students/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['student']
        }),
        deleteStudent: builder.mutation({
            query: (id) => ({
                url: `students/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["student"]
        }),

        // Student Applications endpoints (Müraciətlər)
        getStudentApplications: builder.query({
            query: (params) => ({
                url: `student-applications`,
                params
            }),
            providesTags: ['studentApplication']
        }),
        getStudentApplication: builder.query({
            query: (id) => ({
                url: `student-applications/${id}`
            })
        }),
        addStudentApplication: builder.mutation({
            query: (obj) => ({
                url: `student-applications`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['studentApplication']
        }),
        updateStudentApplication: builder.mutation({
            query: ({ id, obj }) => ({
                url: `student-applications/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['studentApplication']
        }),
        deleteStudentApplication: builder.mutation({
            query: (id) => ({
                url: `student-applications/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["studentApplication"]
        }),

        // Status Change tracking
        addStatusChange: builder.mutation({
            query: (obj) => ({
                url: `status-changes`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['statusChange', 'studentApplication']
        }),

        // University Recommendations
        getUniversityRecommendations: builder.query({
            query: (params) => ({
                url: `university-recommendations`,
                params
            }),
            providesTags: ['university', 'program']
        }),

        // Send University Offers/Recommendations
        sendUniversityOffers: builder.mutation({
            query: (obj) => ({
                url: `university-offers`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['studentApplication', 'universityOffer']
        }),

        // Get University Offers for Application
        getUniversityOffers: builder.query({
            query: (applicationId) => ({
                url: `university-offers/${applicationId}`
            }),
            providesTags: ['universityOffer']
        }),

        // Get Application Status History
        getApplicationStatusHistory: builder.query({
            query: (applicationId) => ({
                url: `application-status-history/${applicationId}`
            }),
            providesTags: ['statusChange', 'studentApplication']
        }),

        // Accept University Offer
        acceptUniversityOffer: builder.mutation({
            query: (obj) => ({
                url: `university-offers/accept`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['studentApplication', 'universityOffer', 'statusChange']
        }),

        // Check student by phone number
        checkStudentByPhone: builder.query({
            query: (phone) => ({
                url: `students-check-phone`,
                params: { phone }
            }),
            providesTags: ['student']
        }),

        // Get application sources
        getApplicationSources: builder.query({
            query: () => ({
                url: `application-sources`
            }),
            providesTags: ['applicationSource']
        }),

        // Get education levels
        getEducationLevels: builder.query({
            query: () => ({
                url: `education-levels`
            }),
            providesTags: ['educationLevel']
        }),

        // Get currencies
        getCurrencies: builder.query({
            query: () => ({
                url: `currencies`
            }),
            providesTags: ['currency']
        }),

        // Contract statuses endpoints
        getContractStatuses: builder.query({
            query: (params) => ({
                url: `contract-statuses`,
                params
            }),
            providesTags: ['contractStatus']
        }),

        // Payment statuses endpoints
        getPaymentStatuses: builder.query({
            query: (params) => ({
                url: `payment-statuses`,
                params
            }),
            providesTags: ['paymentStatus']
        }),

        // Contracts endpoints
        getContracts: builder.query({
            query: (params) => ({
                url: `contracts`,
                params
            }),
            providesTags: ['contract']
        }),
        getActiveContracts: builder.query({
            query: (params) => ({
                url: `contracts-active`,
                params
            }),
            providesTags: ['contract']
        }),
        getContract: builder.query({
            query: (id) => ({
                url: `contracts/${id}`
            })
        }),
        addContract: builder.mutation({
            query: (obj) => ({
                url: `contracts`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['contract']
        }),
        updateContract: builder.mutation({
            query: ({ id, obj }) => ({
                url: `contracts/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['contract']
        }),
        deleteContract: builder.mutation({
            query: (id) => ({
                url: `contracts/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["contract"]
        }),

        // Service Types endpoints
        getServiceTypes: builder.query({
            query: (params) => ({
                url: `service-types`,
                params
            }),
            providesTags: ['serviceType']
        }),
        getActiveServiceTypes: builder.query({
            query: (params) => ({
                url: `service-types-active`,
                params
            }),
            providesTags: ['serviceType']
        }),
        getServiceType: builder.query({
            query: (id) => ({
                url: `service-types/${id}`
            })
        }),
        addServiceType: builder.mutation({
            query: (obj) => ({
                url: `service-types`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['serviceType']
        }),
        updateServiceType: builder.mutation({
            query: ({ id, obj }) => ({
                url: `service-types/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['serviceType']
        }),
        deleteServiceType: builder.mutation({
            query: (id) => ({
                url: `service-types/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["serviceType"]
        }),

        // Services endpoints
        getServices: builder.query({
            query: (params) => ({
                url: `contract-services`,
                params
            }),
            providesTags: ['service']
        }),
        getService: builder.query({
            query: (id) => ({
                url: `contract-services/${id}`
            })
        }),
        addService: builder.mutation({
            query: (obj) => ({
                url: `contract-services`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['service']
        }),
        updateService: builder.mutation({
            query: ({ id, obj }) => ({
                url: `contract-services/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['service']
        }),
        deleteService: builder.mutation({
            query: (id) => ({
                url: `contract-services/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["service"]
        }),

        // Cash Transactions endpoints
        getCashTransactions: builder.query({
            query: (params) => ({
                url: `cash-transactions`,
                params
            }),
            providesTags: ['cashTransaction']
        }),
        getCashTransaction: builder.query({
            query: (id) => ({
                url: `cash-transactions/${id}`
            })
        }),
        addCashTransaction: builder.mutation({
            query: (obj) => ({
                url: `cash-transactions`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['cashTransaction']
        }),
        updateCashTransaction: builder.mutation({
            query: ({ id, obj }) => ({
                url: `cash-transactions/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['cashTransaction']
        }),
        deleteCashTransaction: builder.mutation({
            query: (id) => ({
                url: `cash-transactions/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["cashTransaction"]
        }),

        // Transaction Types endpoints
        

        // Transaction Categories endpoints
        getTransactionCategories: builder.query({
            query: (params) => ({
                url: `transaction-types`,
                params
            }),
            providesTags: ['transactionCategory']
        }),

        // Cash Transaction Payment Methods endpoints
        getCashTransactionPaymentMethods: builder.query({
            query: (params) => ({
                url: `cash-transactions-payment-methods`,
                params
            }),
            providesTags: ['paymentMethod']
        }),
        addPaymentMethod: builder.mutation({
            query: (obj) => ({
                url: `payment-methods`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['paymentMethod']
        }),
        updatePaymentMethod: builder.mutation({
            query: ({id, obj}) => ({
                url: `payment-methods/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['paymentMethod']
        }),
        deletePaymentMethod: builder.mutation({
            query: (id) => ({
                url: `payment-methods/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: ['paymentMethod']
        }),
        addTransactionCategory: builder.mutation({
            query: (obj) => ({
                url: `transaction-types`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['transactionCategory']
        }),
        updateTransactionCategory: builder.mutation({
            query: ({ id, obj }) => ({
                url: `transaction-types/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['transactionCategory']
        }),
        deleteTransactionCategory: builder.mutation({
            query: (id) => ({
                url: `transaction-types/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["transactionCategory"]
        }),

        // Tasks endpoints
        getTasks: builder.query({
            query: (params) => ({
                url: `tasks`,
                params
            }),
            providesTags: ['task']
        }),
        getTask: builder.query({
            query: (id) => ({
                url: `tasks/${id}`
            })
        }),
        addTask: builder.mutation({
            query: (obj) => ({
                url: `tasks`,
                method: "POST",
                body: obj
            }),
            invalidatesTags: ['task']
        }),
        updateTask: builder.mutation({
            query: ({ id, obj }) => ({
                url: `tasks/${id}`,
                method: "PUT",
                body: obj
            }),
            invalidatesTags: ['task']
        }),
        deleteTask: builder.mutation({
            query: (id) => ({
                url: `tasks/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["task"]
        }),
    })
})

export const {
    useFetchLoginMutation,
    useGetUniversitiesQuery,
    useGetActiveUniversitiesQuery,
    useGetUniversityQuery,
    useAddUniversityMutation,
    useUpdateUniversityMutation,
    useDeleteUniversityMutation,
    useGetCountriesQuery,
    useGetCitiesQuery,
    useUpdateCountryMutation,
    useUpdateCityMutation,
    useAddCityMutation,
    useGetCityQuery,
    useGetCountryQuery,
    useDeleteCityMutation,
    useGetActiveCountriesQuery,
    useDeleteCountryMutation,
    useAddCountryMutation,
    useGetProgramsQuery,
    useGetActiveProgramsQuery,
    useGetProgramQuery,
    useAddProgramMutation,
    useUpdateProgramMutation,
    useDeleteProgramMutation,
    useGetStudentRequestStatusesQuery,
    useGetStudentRequestStatusQuery,
    useAddStudentRequestStatusMutation,
    useUpdateStudentRequestStatusMutation,
    useDeleteStudentRequestStatusMutation,
    useGetScholarshipsQuery,
    useGetActiveScholarshipsQuery,
    useGetScholarshipQuery,
    useAddScholarshipMutation,
    useUpdateScholarshipMutation,
    useDeleteScholarshipMutation,
    useGetUsersQuery,
    useGetActiveUsersQuery,
    useGetUserQuery,
    useAddUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useGetRolesQuery,
    useGetRoleQuery,
    useAddRoleMutation,
    useUpdateRoleMutation,
    useDeleteRoleMutation,
    useGetPermissionsQuery,
    useGetPermissionQuery,
    useGetGroupedPermissionsQuery,
    useGetPermissionModulesQuery,
    useAddPermissionMutation,
    useUpdatePermissionMutation,
    useDeletePermissionMutation,
    useGetConsultantsQuery,
    useGetConsultantQuery,
    useAddConsultantMutation,
    useUpdateConsultantMutation,
    useDeleteConsultantMutation,
    useGetConsultantAdminsQuery,
    useGetStudentsQuery,
    useGetActiveStudentsQuery,
    useGetStudentQuery,
    useAddStudentMutation,
    useUpdateStudentMutation,
    useDeleteStudentMutation,
    useGetStudentApplicationsQuery,
    useGetStudentApplicationQuery,
    useAddStudentApplicationMutation,
    useUpdateStudentApplicationMutation,
    useDeleteStudentApplicationMutation,
    useAddStatusChangeMutation,
    useGetUniversityRecommendationsQuery,
    useSendUniversityOffersMutation,
    useGetUniversityOffersQuery,
    useGetApplicationStatusHistoryQuery,
    useAcceptUniversityOfferMutation,
    useCheckStudentByPhoneQuery,
    useGetApplicationSourcesQuery,
    useGetEducationLevelsQuery,
    useGetCurrenciesQuery,
    useGetContractStatusesQuery,
    useGetPaymentStatusesQuery,
    useGetContractsQuery,
    useGetActiveContractsQuery,
    useGetContractQuery,
    useAddContractMutation,
    useUpdateContractMutation,
    useDeleteContractMutation,
    useGetServiceTypesQuery,
    useGetActiveServiceTypesQuery,
    useGetServiceTypeQuery,
    useAddServiceTypeMutation,
    useUpdateServiceTypeMutation,
    useDeleteServiceTypeMutation,
    useGetServicesQuery,
    useGetServiceQuery,
    useAddServiceMutation,
    useUpdateServiceMutation,
    useDeleteServiceMutation,
    useGetCashTransactionsQuery,
    useGetCashTransactionQuery,
    useAddCashTransactionMutation,
    useUpdateCashTransactionMutation,
    useDeleteCashTransactionMutation,
    useGetTransactionTypesQuery,
    useGetTransactionCategoriesQuery,
    useAddTransactionCategoryMutation,
    useUpdateTransactionCategoryMutation,
    useDeleteTransactionCategoryMutation,
    useGetCashTransactionPaymentMethodsQuery,
    useAddPaymentMethodMutation,
    useUpdatePaymentMethodMutation,
    useDeletePaymentMethodMutation,
    useGetTasksQuery,
    useGetTaskQuery,
    useAddTaskMutation,
    useUpdateTaskMutation,
    useDeleteTaskMutation
} = api